export interface RevertClipSource {
  original_clip_start_time?: number | null;
  original_clip_end_time?: number | null;
  clip_start_time?: number | null;
  clip_end_time?: number | null;
  data?: {
    segments?: Array<{ start?: number | null; end?: number | null }> | null;
  } | null;
}

export interface RevertRange {
  start: number;
  end: number;
}

/**
 * Resolves the video-time range(s) "Revert to original" should restore for a
 * clip, in priority order:
 *
 * 1. The backend-preserved AI bounds (`original_clip_start_time/end_time`).
 * 2. The full span of `data.segments` (min start → max end). Spanning ALL
 *    segments undoes automatic decoupage cuts — `clip_start_time/end_time`
 *    cannot be used for this because the backend computes them from
 *    `data.segments[0]` alone, which reverts to just the first cut.
 * 3. `clip_start_time/end_time`, for clips with no segment data at all
 *    (e.g. curated_clips JSON entries).
 */
export function resolveRevertRanges(
  clip: RevertClipSource | null | undefined,
): RevertRange[] {
  if (!clip) return [];

  if (
    typeof clip.original_clip_start_time === "number" &&
    typeof clip.original_clip_end_time === "number"
  ) {
    return [
      { start: clip.original_clip_start_time, end: clip.original_clip_end_time },
    ];
  }

  const segments = clip.data?.segments;
  if (Array.isArray(segments)) {
    let start = Infinity;
    let end = -Infinity;
    for (const seg of segments) {
      if (typeof seg?.start === "number" && typeof seg?.end === "number") {
        start = Math.min(start, seg.start);
        end = Math.max(end, seg.end);
      }
    }
    if (start !== Infinity && end !== -Infinity) {
      return [{ start, end }];
    }
  }

  if (
    typeof clip.clip_start_time === "number" &&
    typeof clip.clip_end_time === "number"
  ) {
    return [{ start: clip.clip_start_time, end: clip.clip_end_time }];
  }

  return [];
}
